import React, { useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { PropTypes } from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { FormGrid } from "../../../../../components";

const ReferencesDialog = ({
  onClose,
  open = false,
  readonly,
  reference = {},
}) => {
  const formMethods = useForm({
    defaultValues: {
      author: "",
      citation: "",
      commissioned: false,
      copyrightYear: null,
      ISBN: "",
      notes: "",
      page: "",
      publicationInformation: "",
      publicDomain: false,
      publisher: "",
      referenceTitle: "",
      rightsHolder: "",
      sourceURL: "",
      wordCount: null,
      workTitle: "",
      ...reference,
    },
    mode: "all",
    reValidateMode: "onBlur",
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = formMethods;

  const action = useMemo(() => {
    if (!Object.hasOwn(reference, "referenceTitle")) return "Add";
    if (readonly) return "View";
    return "Update";
  }, [readonly, reference]);

  const handleClose = () => {
    onClose();
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const onSubmit = (data) => {
    onClose(data);
  };

  return (
    <FormProvider {...formMethods}>
      <Dialog maxWidth="lg" onClose={handleClose} open={open}>
        <DialogTitle>{action} Reference Citation</DialogTitle>
        <DialogContent dividers={true} sx={{ display: "flex", height: 500 }}>
          <form
            id="referencesForm"
            onSubmit={handleModalSubmit}
            style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
          >
            {controls.map((props) => (
              <FormGrid
                control={control}
                id={props.name}
                key={props.name}
                readonly={readonly}
                {...props}
              />
            ))}
          </form>
        </DialogContent>
        <DialogActions>
          {!readonly && (
            <Button onClick={() => handleClose()} variant="text">
              Cancel
            </Button>
          )}
          <Button
            color="primary"
            disabled={!isValid}
            form="referencesForm"
            type="submit"
            variant="contained"
          >
            {readonly ? "Close" : action}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const controls = [
  {
    label: "Reference Title",
    name: "referenceTitle",
    rules: {
      maxLength: 150,
      required: true,
    },
    type: "text",
  },
  {
    label: "Work Title",
    name: "workTitle",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Author",
    name: "author",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Publication Information",
    name: "publicationInformation",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Page",
    name: "page",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Copyright Year",
    multiline: false,
    name: "copyrightYear",
    rules: {
      maxLength: 4,
    },
    type: "integer",
  },
  {
    label: "Rights Holder",
    name: "rightsHolder",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Publisher",
    name: "publisher",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Source URL",
    multiline: false,
    name: "sourceURL",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "ISBN",
    name: "ISBN",
    rules: {
      maxLength: 20,
    },
    type: "text",
  },
  {
    label: "Word Count",
    multiline: false,
    name: "wordCount",
    rules: {
      maxLength: 5,
    },
    type: "integer",
  },
  {
    label: "Public Domain",
    name: "publicDomain",
    type: "switch",
  },
  {
    label: "Commissioned",
    name: "commissioned",
    type: "switch",
  },
  {
    label: "Notes",
    name: "notes",
    rules: {
      maxLength: 150,
    },
    type: "text",
  },
  {
    label: "Citation",
    name: "citation",
    type: "editor",
  },
];

ReferencesDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  readonly: PropTypes.bool.isRequired,
  reference: PropTypes.shape({
    author: PropTypes.string,
    citation: PropTypes.string,
    commissioned: PropTypes.bool,
    copyrightYear: PropTypes.number,
    ISBN: PropTypes.string,
    notes: PropTypes.string,
    page: PropTypes.string,
    publicationInformation: PropTypes.string,
    publicDomain: PropTypes.bool,
    publisher: PropTypes.string,
    referenceTitle: PropTypes.string.isRequired,
    rightsHolder: PropTypes.string,
    sourceURL: PropTypes.string,
    wordCount: PropTypes.number,
    workTitle: PropTypes.string,
  }),
};

export { ReferencesDialog };
